import { createSelector } from '@reduxjs/toolkit'

import { RootState } from '../configure_store'
import { getProducts, getAdviceProducts } from './products'
import { decode } from 'html-entities'
import { getLicensePlate } from '../../utils/policy_utils'
import { getPullingVehicleBranches, getTowedVehicleBranches } from './ivb'
import { IconProp } from '@fortawesome/fontawesome-svg-core'

const _getState = (state: RootState) => state.policies

export const getPolicies = createSelector(_getState, state => state.policies)

export const getPolicyById = (id: string) =>
  createSelector(_getState, state =>
    state.policies.find(policy => policy.id === id)
  )

export const getPolicyDetails = createSelector(_getState, state => state.policyDetails)

export const getSelectedPolicy = createSelector(_getState, state =>
  state.policies.find(policy => policy.policyNumber === state.selectedPolicyNumber)
)

export const getGreencardPolicies = createSelector(_getState, state =>
  state.policies.filter(policy => !!policy.greenCard)
)

export const getProductClaimType = createSelector(
  getSelectedPolicy,
  getProducts,
  (policy, products) => products.find(product => {
    if (!product?.branches) return false

    const claimMethod = product?.branches?.find(
      x => String(x.brancheCode) === String(policy?.branche)
    )

    // If no claim method found, return false
    return !!claimMethod
  })
)

export const getClaimMethod = createSelector(
  getSelectedPolicy,
  getProductClaimType,
  (policy, product) => {
    const claimMethod = product?.branches?.find(
      x => String(x.brancheCode) === String(policy?.branche)
    )

    return {
      ...claimMethod,
      description: decode(claimMethod?.description ?? '')
    }
  }
)

export const getProductIcons = createSelector(
  getPolicies,
  getProducts,
  getAdviceProducts,
  (policies, products, adviceProducts) => {
    const icons: { [key: string]: IconProp }  = {}

    policies.forEach(policy => {
      const product = products.find(product => {
        if (!product?.branches) return false

        const branch = product?.branches?.find(
          x => String(x.brancheCode) === String(policy?.branche)
        )

        return !!branch
      })

      const adviceProduct = adviceProducts.find(ap => ap.url === product?.url)

      icons[policy.productId] = (adviceProduct?.icon || 'file-lines') as IconProp
    })

    return icons
  }
)

export const getPoliciesUnexpelled = createSelector(getPolicies, policies => {
  if (!policies) return []

  return policies.filter(({ status }) => status !== 'Geroyeerd')
})

export const getLastThreeUnexpelledPolicies = createSelector(
  getPoliciesUnexpelled,
  policies => policies.slice(0, 3)
)

export const getIsPolicyPullable = (policyId: string) =>
  createSelector(
    getPolicyById(policyId),
    getTowedVehicleBranches,
    (policy, pullableVehicleBranches) => (
      policy &&
        pullableVehicleBranches.includes(policy?.branche) &&
        policy?.status === 'Lopend' &&
        getLicensePlate(policy?.details) !== null
    )

  )

export const getPoliciesActivePullingVehicles = createSelector(
  getPolicies,
  getPullingVehicleBranches,
  (policies, pullingVehicleBranches) => {
    if (!policies) return []

    return policies.filter(
      ({ branche, status, details }) =>
        pullingVehicleBranches.includes(branche) &&
        status === 'Lopend' &&
        getLicensePlate(details) !== null
    )
  })

export const getPoliciesActiveTowedVehicles = createSelector(
  getPolicies,
  getTowedVehicleBranches,
  (policies, towedVehicleBranches) => {
    if (!policies) return []

    return policies.filter(
      ({ branche, status, details }) =>
        towedVehicleBranches.includes(branche) &&
        status === 'Lopend' &&
        getLicensePlate(details) !== null
    )
  }
)

// Policies that are neither pulling nor towed vehicles
// but are still active and have a license plate and also have an green card
export const getPoliciesNonIVBVehicles = createSelector(
  getPolicies,
  getPullingVehicleBranches,
  getTowedVehicleBranches,
  (policies, pullingVehicleBranches, towedVehicleBranches) => {
    if (!policies) return []

    return policies.filter(
      ({ branche, status, details, greenCard, greenCards }) =>
        !pullingVehicleBranches.includes(branche) &&
        !towedVehicleBranches.includes(branche) &&
        status === 'Lopend' &&
        getLicensePlate(details) !== null &&
        (greenCard !== undefined || (greenCards !== undefined && greenCards.length > 0))
    )
  }
)

export const getPoliciesActiveIVBVehicles = createSelector(
  getPolicies,
  getPoliciesActivePullingVehicles,
  getPoliciesActiveTowedVehicles,
  getPoliciesNonIVBVehicles,
  (policies, pullingVehicles, towedVehicles, nonIVBVehicles) =>
    [...pullingVehicles, ...towedVehicles, ...nonIVBVehicles].sort(
      // Sort based on policies array order
      (a, b) => policies.findIndex(p => p.id === a.id) - policies.findIndex(p => p.id === b.id)
    )
)

export const getPoliciesExpelled = createSelector(getPolicies, policies => {
  if (!policies) return []

  return policies.filter(({ status }) => status === 'Geroyeerd')
})

export const getIsPoliciesLoading = createSelector(_getState, state => state.isLoading)

export const getCombiCollectionAmount = createSelector(
  _getState,
  state => state.combiCollectionAmount
)
